import { Braces } from "lucide-react";
import { useMemo } from "react";

import { getRequestVolumeSignalColor } from "./useRequestVolumeTailChartOptions";

import { EntityIcon } from "@/components/ui/icons";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { EntityType } from "@/queries";
import type {
  GetRequestVolumeTailResponse,
  RequestVolumeTailTemplateSummary,
} from "@/queries/signals";

interface TemplateBreakdownProps {
  data: GetRequestVolumeTailResponse | undefined;
  selectedPromptId?: number | null;
  onTemplateClick?: (summary: RequestVolumeTailTemplateSummary) => void;
}

const getRate = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 100) : 0;

const TemplateBreakdown = ({
  data,
  selectedPromptId = null,
  onTemplateClick,
}: TemplateBreakdownProps) => {
  const summaries = data?.template_summaries ?? [];
  const signalNamesById = useMemo(
    () => new Map((data?.signals ?? []).map(signal => [signal.id, signal.name])),
    [data?.signals],
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <h3 className="mb-2 shrink-0 text-xs font-medium text-gray-700">Templates</h3>

      {summaries.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center px-2 text-[11px] leading-relaxed text-gray-400">
          No template activity in this live window.
        </div>
      ) : (
        <div
          className="scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden pr-1"
          style={{ scrollbarGutter: "stable" }}
        >
          {summaries.map(summary => {
            const selected = selectedPromptId === summary.prompt_id;
            const positiveSignals = summary.signal_counts
              .filter(signal => signal.count > 0)
              .map(signal => ({
                ...signal,
                name: signalNamesById.get(signal.signal_id) ?? `Signal #${signal.signal_id}`,
              }))
              .sort(
                (left, right) => right.count - left.count || left.name.localeCompare(right.name),
              );
            const topSignals = positiveSignals.slice(0, 2);
            const remainingSignalCount = Math.max(0, positiveSignals.length - topSignals.length);
            const disabled = !onTemplateClick;
            return (
              <button
                key={summary.prompt_id ?? "direct"}
                type="button"
                className="group flex w-full min-w-0 flex-col rounded-md px-2 py-1.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent"
                onClick={() => onTemplateClick?.(summary)}
                disabled={disabled}
                title={`Focus ${summary.prompt_name}`}
              >
                <span className="flex w-full min-w-0 items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-50">
                    {summary.prompt_id === null ? (
                      <Braces className="h-3 w-3 text-gray-400" />
                    ) : (
                      <EntityIcon type={EntityType.PROMPT} size="xs" />
                    )}
                  </span>
                  <OverflowDisplayTooltip content={summary.prompt_name} className="min-w-0 flex-1">
                    <span
                      className={
                        selected
                          ? "block truncate text-[11px] font-medium text-gray-900"
                          : "block truncate text-[11px] text-gray-500 group-hover:text-gray-800"
                      }
                    >
                      {summary.prompt_name}
                    </span>
                  </OverflowDisplayTooltip>
                  <span className="shrink-0 text-[10px] tabular-nums text-gray-400">
                    {summary.total_requests}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 pl-7 text-[10px] text-gray-400">
                  <span>{summary.flagged_requests} matches</span>
                  <span aria-hidden="true">·</span>
                  <span>{getRate(summary.flagged_requests, summary.total_requests)}%</span>
                </span>
                {topSignals.length > 0 ? (
                  <span className="mt-1 flex min-w-0 flex-wrap gap-1 pl-7">
                    {topSignals.map(signal => {
                      const signalLabel = `${signal.name} ${signal.count}`;
                      return (
                        <OverflowDisplayTooltip
                          key={signal.signal_id}
                          content={signalLabel}
                          className="max-w-full"
                        >
                          <span
                            className="block max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
                            style={{
                              backgroundColor: getRequestVolumeSignalColor(signal.signal_id),
                            }}
                          >
                            {signalLabel}
                          </span>
                        </OverflowDisplayTooltip>
                      );
                    })}
                    {remainingSignalCount > 0 ? (
                      <span className="rounded bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-500">
                        +{remainingSignalCount} more
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TemplateBreakdown;
